import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useUser } from '../context/UserContext';
import { sendDemoVerificationEmail, sendDemoWelcomeEmail } from '../services/userService';

const VerifyEmailPage = () => {
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [checkLoading, setCheckLoading] = useState(false);
  const navigate = useNavigate();
  const { refreshUser } = useUser();
  const location = useLocation();
  const emailAddress = location.state?.email || auth.currentUser?.email || '';

  const handleResend = async () => {
    setResendMessage('');
    setResendLoading(true);
    try {
      await sendDemoVerificationEmail();
      setResendMessage('Verification email sent. Please check your inbox.');
    } catch (err) {
      if (err.message?.includes('too-many-requests') || err.status === 429) {
        setResendMessage('Too many requests. Please wait a moment and try again.');
      } else {
        setResendMessage('Failed to resend. Please try again later.');
      }
    } finally {
      setResendLoading(false);
    }
  };

  const handleCheckVerified = async () => {
    setCheckLoading(true);
    console.log('[VerifyEmail] handleCheckVerified called');
    try {
      const user = auth.currentUser;
      console.log('[VerifyEmail] currentUser:', user?.uid, 'emailVerified before reload:', user?.emailVerified);
      if (user) {
        await user.reload();
        console.log('[VerifyEmail] After reload, emailVerified:', user.emailVerified);
        if (user.emailVerified) {
          console.log('[VerifyEmail] Verified — sending welcome email, refreshing userInfo then navigating to /');
          // Fire-and-forget: welcome email failure should not block navigation
          sendDemoWelcomeEmail().catch((err) => console.warn('[VerifyEmail] Welcome email failed:', err));
          await refreshUser();
          navigate('/');
        } else {
          console.log('[VerifyEmail] Not yet verified');
          setResendMessage('Email not yet verified. Please check your inbox and click the verification link.');
        }
      } else {
        console.log('[VerifyEmail] No currentUser found');
      }
    } catch (err) {
      console.error('[VerifyEmail] Error during reload:', err);
      setResendMessage('Could not check verification status. Please try again.');
    } finally {
      setCheckLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
      <div className="bg-white shadow-md rounded-lg p-8 w-full max-w-md text-center">
        <div className="text-5xl mb-4">📧</div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Verify your email</h2>
        <p className="text-gray-600 mb-6">
          A verification link was sent to{emailAddress ? <> <strong>{emailAddress}</strong>.</> : ' your email address.'} Please check your inbox (and spam folder) and click the link to continue.
        </p>

        {resendMessage && (
          <div className="bg-blue-50 border border-blue-200 text-blue-700 rounded px-4 py-3 mb-4 text-sm">
            {resendMessage}
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={handleCheckVerified}
            disabled={checkLoading}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 rounded-md transition-colors disabled:opacity-60"
          >
            {checkLoading ? 'Checking…' : "I've verified my email"}
          </button>

          <button
            onClick={handleResend}
            disabled={resendLoading}
            className="w-full bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-2 rounded-md transition-colors disabled:opacity-60"
          >
            {resendLoading ? 'Sending…' : 'Resend verification email'}
          </button>

          <button
            onClick={handleSignOut}
            className="w-full text-gray-400 hover:text-gray-600 text-sm py-1 transition-colors"
          >
            Sign out and use a different account
          </button>
        </div>


      </div>
    </div>
  );
};

export default VerifyEmailPage;
